/**
 * Fetch wrapper that attaches the current Company-issued JWT and handles
 * the two failure modes the new auth model introduces:
 *
 *   - **401** — JWT expired or absent. We try a silent refresh via
 *     `companyAuth.refreshJwt()` (driven by `triggerRefresh`); on success
 *     we replay the original request once. On failure we fire the
 *     session-lost callback so AuthContext can surface the login modal.
 *
 *   - **409 `username_unclaimed`** — first call after Company signup. The
 *     server returns the Company user id; we surface it via
 *     `notifyUnclaimed` so the global ClaimUsernameModal can prompt for
 *     a handle. The original request still returns its 409 response — the
 *     caller can ignore it; once the user claims, the page reloads or the
 *     caller re-fetches.
 */

import {
  getJwt,
  notifySessionLost,
  notifyUnclaimed,
  triggerRefresh,
} from './jwtHolder';

let refreshing: Promise<string | null> | null = null;

function buildHeaders(input: HeadersInit | undefined, jwt: string | null): HeadersInit {
  const headers = new Headers(input);
  if (jwt && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${jwt}`);
  }
  return headers;
}

async function dispatch(url: RequestInfo | URL, options: RequestInit): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: buildHeaders(options.headers, getJwt()),
  });
}

async function maybeHandleClaim(response: Response): Promise<void> {
  if (response.status !== 409) return;
  try {
    const body = await response.clone().json();
    if (body?.code === 'username_unclaimed' && typeof body?.companyUserId === 'string') {
      notifyUnclaimed(body.companyUserId);
    }
  } catch {
    // Non-JSON 409 — not our claim flow, leave it for the caller.
  }
}

export async function fetchWithAuth(
  url: RequestInfo | URL,
  options: RequestInit = {},
): Promise<Response> {
  const first = await dispatch(url, options);

  if (first.status === 409) {
    await maybeHandleClaim(first);
    return first;
  }

  if (first.status !== 401) return first;

  // 401: try one silent refresh and replay once. Concurrent 401s share the
  // same in-flight refresh promise so we don't stampede Company's API.
  if (!refreshing) {
    refreshing = triggerRefresh().finally(() => {
      refreshing = null;
    });
  }
  const fresh = await refreshing;
  if (!fresh) {
    notifySessionLost();
    return first;
  }
  return dispatch(url, options);
}
